import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isInteger from 'lodash/isInteger';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import React from 'react';

import { ListErrors } from 'src/components/common/Errors/list';
import { UiToast } from 'src/components/UI/toasts';
import { BaseApiResponseErrorsType, CommonApiResponseType, PreparedApiErrorsType } from 'src/models/BaseApi';
import { isHttpResponseValid } from 'src/utils/httpResponse';
import { removeTags } from 'src/utils/stringArrayStartsWith';

/**
 * the function which tries to simplify errors (to get only text messages) from a given response
 *
 * the second argument is internal. do not call this function with it
 */
export const getErrorTextFromPreparedApiResponse = (
  data: CommonApiResponseType | PreparedApiErrorsType,
  possibleArrayOfStringsWhenInterateFirstTime = true
): string[] => {
  if (isString(data)) {
    return [removeTags(data)];
  }

  const arr: string[] = [];

  if (isArray(data) || isPlainObject(data)) {
    forEach(data, (item, key) => {
      /** string of array or key is 'message' */
      if ((possibleArrayOfStringsWhenInterateFirstTime && isString(item) && isInteger(key)) || key === 'message') {
        arr.push(removeTags(item));
      } else if (isArray(item) || isPlainObject(item)) {
        arr.push(...getErrorTextFromPreparedApiResponse(item, false));
      }
    });
  }

  return arr;
};

/**
 * the function which shows any given errors
 */
export const showAnyErrors = (data: CommonApiResponseType | PreparedApiErrorsType): void => {
  const messages = getErrorTextFromPreparedApiResponse(data);

  if (messages.length > 0) {
    UiToast.error(messages.length === 1 ? messages[0] : <ListErrors errorMessages={messages} />);
  }
};

/**
 * recursively get all errors from a given response
 * there will always be properites having errors inside themselves, don't matter how deep they are nested
 *
 * if you need only text messages use `getErrorTextFromPreparedApiResponse` instead
 */
export const convertRawApiErrorResponseToPreparedApiErrors = (
  response: CommonApiResponseType
): PreparedApiErrorsType => {
  let errors: PreparedApiErrorsType = [];

  /** look at root errors first */
  if (get(response, 'errors.length') > 0) {
    if (isArray(response.errors)) {
      errors = [...cloneDeep(response.errors)];
    } else {
      errors.push(response.errors);
    }
  }

  /** go by every property searching errors */
  forEach(response, (iteratingItem, key) => {
    const errorsObjectsFromResponse: Record<string, CommonApiResponseType> = {};

    if (isPlainObject(iteratingItem) && !isHttpResponseValid(iteratingItem)) {
      errorsObjectsFromResponse[key] = iteratingItem;
    } else if (Array.isArray(iteratingItem)) {
      iteratingItem.forEach((objFromArrayItem) => {
        if (isPlainObject(objFromArrayItem) && !isHttpResponseValid(objFromArrayItem)) {
          errorsObjectsFromResponse[key] = iteratingItem;
        }
      });
    }

    if (!isEmpty(errorsObjectsFromResponse)) {
      errors.push(errorsObjectsFromResponse);
    }
  });

  return errors;
};

export type PreparedFormErrorsType = { key: string; message: string }[];

/**
 * creates an array of prepared objects that can be used in setting errors to stateForm
 */
export const convertPreparedApiErrorsToFormErrors = (
  errors: PreparedApiErrorsType,
  replacer: (propName: string) => string = (v) => v,
  noBrackets = false
): PreparedFormErrorsType => {
  const preparedFormNames: PreparedFormErrorsType = [];

  if (!isArray(errors)) {
    errors = [errors];
  }

  errors.forEach((innerError) => {
    if (isPlainObject(innerError)) {
      innerError = innerError as Omit<PreparedApiErrorsType, 'string'>;

      if (innerError.errors && innerError.errors.length > 0) {
        const { property, errors } = innerError as BaseApiResponseErrorsType[0];
        const properties = property.split(',');

        properties.forEach((propName: string) => {
          preparedFormNames.push({ key: replacer(propName), message: getErrorTextFromPreparedApiResponse(errors)[0] });
        });
      } else {
        Object.entries(innerError).forEach(([propName, innerObjError]) => {
          if (Array.isArray(innerObjError)) {
            innerObjError.forEach((innerArrError, index) => {
              if (!isHttpResponseValid(innerArrError)) {
                if (innerArrError.errors && innerArrError.errors.length > 0) {
                  innerArrError.errors.forEach(({ property, errors }: BaseApiResponseErrorsType[0]) => {
                    const properties = property.split(',');

                    properties.forEach((innerPropName: string) => {
                      preparedFormNames.push({
                        // TODO: replace [] with dot
                        key: noBrackets
                          ? `${replacer(propName)}.${index}.${innerPropName}`
                          : `${replacer(propName)}[${index}].${innerPropName}`,
                        message: getErrorTextFromPreparedApiResponse(errors)[0] || 'anyerror',
                      });
                    });
                  });
                } else {
                  // take inner errors and concat them with the current position
                  convertPreparedApiErrorsToFormErrors(innerArrError, undefined, noBrackets).forEach((error) => {
                    preparedFormNames.push({
                      // TODO: replace [] with dot
                      key: noBrackets
                        ? `${replacer(propName)}.${index}.${error.key}`
                        : `${replacer(propName)}[${index}].${error.key}`,
                      message: error.message,
                    });
                  });
                }
              }
            });
          } else if (isPlainObject(innerObjError)) {
            if (innerObjError.errors && innerObjError.errors.length > 0) {
              convertPreparedApiErrorsToFormErrors(innerObjError.errors, undefined, noBrackets).forEach(
                ({ key, message }) => {
                  preparedFormNames.push({
                    key: replacer(`${propName}.${key}`),
                    message,
                  });
                }
              );
            } else {
              convertPreparedApiErrorsToFormErrors([innerObjError], undefined, noBrackets).forEach(
                ({ key, message }) => {
                  preparedFormNames.push({
                    key: replacer(`${propName}.${key}`),
                    message,
                  });
                }
              );
            }
          }
        });
      }
    } else if (isString(innerError)) {
      preparedFormNames.push({
        key: innerError,
        message: innerError,
      });
    }
  });

  return preparedFormNames;
};
